import { Observable, of, switchMap, tap, catchError, throwError } from 'rxjs';

import { CacheManagerService } from '../services/cache-manager.service';

export interface IInvalidationConfig {
  projectId: string;
  itemId: string | number;
  lookupType?: 'id' | 'slug';
}

/**
 * Interface defining the structure of cache operators returned by the factory function.
 */
export interface ICacheOperators {
  /**
   * Conditionally uses cache based on store availability.
   * When no store is available, goes straight to API.
   * When store is available, tries cache first, checks for invalidation, then falls back to API if needed.
   */
  cacheOrFetch: <T>(storeName: string, apiFallback: () => Observable<T>, invalidationConfig?: IInvalidationConfig) => Observable<T>;

  /**
   * Conditionally updates cache after successful operations.
   * Only performs cache updates when store is available.
   */
  withCacheUpdate: <T>(source: Observable<T>, storeName: string, hydrateCallback?: (item: T) => T) => Observable<T>;

  /**
   * Removes an item from cache after successful operations.
   * Only performs cache removal when store is available.
   */
  withCacheRemoval: <T>(source: Observable<T>, storeName: string, itemId: string | number) => Observable<T>;

  /**
   * Removes multiple items from cache after successful operations.
   * Only performs cache removal when store is available.
   */
  withCacheMultiRemoval: <T>(source: Observable<T>, storeName: string, itemIds: (string | number)[]) => Observable<T>;
}

/**
 * Cache operators factory that creates cache operation functions with injected dependencies.
 * This approach avoids injection context issues while providing clean, reusable cache operations.
 *
 * @param cacheManager The cache manager service instance.
 * @returns Object containing all cache operation functions.
 */
export const createCacheOperators = (cacheManager: CacheManagerService): ICacheOperators => {
  return {
    /**
     * Conditionally uses cache based on store availability.
     * When no store is available, goes straight to API.
     * When store is available, tries cache first, checks for invalidation, then falls back to API if needed.
     *
     * @param storeName Name of the cache store to use.
     * @param apiFallback Function that returns the API call Observable.
     * @param invalidationConfig Optional configuration for auto-invalidation checking.
     *
     * @returns Observable that emits the cached or fetched data.
     */
    cacheOrFetch: <T>(storeName: string, apiFallback: () => Observable<T>, invalidationConfig?: IInvalidationConfig): Observable<T> => {
      const store = cacheManager.getStore<T>(storeName);

      // If no store available, go straight to API
      if (!store) {
        return apiFallback();
      }

      // If no item ID provided, can't use cache effectively
      if (!invalidationConfig?.itemId) {
        return apiFallback();
      }

      // Determine lookup method based on lookupType
      const lookupType = invalidationConfig.lookupType || 'id';
      const cacheObservable = lookupType === 'slug'
        ? store.getBySlug(invalidationConfig.itemId as string, invalidationConfig.projectId)
        : store.get(invalidationConfig.itemId);

      return cacheObservable.pipe(
        switchMap(async(cached) => {
          // If no cached item, fetch from API
          if (!cached) {
            return apiFallback();
          }

          // Check if item needs refresh (if project ID is provided)
          if (invalidationConfig.projectId) {
            try {
              // For slug lookups, we need to use the actual item ID for invalidation checking
              const itemIdForInvalidation = lookupType === 'slug' && cached && typeof cached === 'object' && 'id' in cached
                ? String(cached.id)
                : String(invalidationConfig.itemId);

              const needsRefresh = await store.needsRefresh(
                itemIdForInvalidation,
                invalidationConfig.projectId
              );

              if (needsRefresh) {
                return apiFallback();
              }
            } catch (error) {
              // On error, fall back to API
              return apiFallback();
            }
          }

          // Return cached item
          return of(cached);
        }),
        switchMap((result) => result),
        catchError(() => {
          // On any error, try API as fallback
          return apiFallback();
        })
      );
    },

    /**
     * Conditionally updates cache after successful operations.
     * Only performs cache updates when store is available.
     *
     * @param source The source Observable to wrap with cache update functionality.
     * @param storeName Name of the cache store to update.
     * @param hydrateCallback Optional function to transform/hydrate data before caching.
     *
     * @returns Observable that emits the original data and updates cache as a side effect.
     */
    withCacheUpdate: <T>(source: Observable<T>, storeName: string, hydrateCallback?: (item: T) => T): Observable<T> => {
      const store = cacheManager.getStore<T>(storeName);

      // If no store available, just return source unchanged
      if (!store) {
        return source;
      }

      return source.pipe(
        tap(item => {
          const itemToCache = hydrateCallback ? hydrateCallback(item) : item;

          store.set(itemToCache).subscribe({
            error: () => throwError
          });
        })
      );
    },

    /**
     * Removes an item from cache after successful operations.
     * Only performs cache removal when store is available.
     *
     * @param source The source Observable to wrap with cache removal functionality.
     * @param storeName
     * @param itemId ID of the item to remove from cache.
     *
     * @returns Observable that emits the original data and removes item from cache as a side effect.
     */
    withCacheRemoval: <T>(source: Observable<T>, storeName: string, itemId: string | number): Observable<T> => {
      const store = cacheManager.getStore(storeName);

      // If no store available, just return source unchanged
      if (!store) {
        return source;
      }

      return source.pipe(
        tap(() => {
          store.remove(itemId).subscribe({
            error: () => throwError
          });
        })
      );
    },

    /**
     * Removes multiple items from cache after successful operations.
     * Only performs cache removal when store is available.
     *
     * @param source The source Observable to wrap with cache removal functionality.
     * @param storeName
     * @param itemIds Array of item IDs to remove from cache.
     *
     * @returns Observable that emits the original data and removes items from cache as a side effect.
     */
    withCacheMultiRemoval: <T>(source: Observable<T>, storeName: string, itemIds: (string | number)[]): Observable<T> => {
      const store = cacheManager.getStore(storeName);

      // If no store available, just return source unchanged
      if (!store) {
        return source;
      }

      return source.pipe(
        tap(() => {
          itemIds.forEach(itemId => {
            store.remove(itemId).subscribe({
              error: () => throwError
            });
          });
        })
      );
    }
  };
};
